package co.maytes.standupbot.dashboard.pages.app.standupruns

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import co.maytes.standupbot.dashboard.components.feedback.ApiErrorView
import co.maytes.standupbot.dashboard.components.feedback.SkeletonBox
import co.maytes.standupbot.dashboard.components.feedback.SkeletonCard
import co.maytes.standupbot.dashboard.components.feedback.SkeletonLine
import co.maytes.standupbot.dashboard.components.layout.AppShell
import co.maytes.standupbot.dashboard.components.layout.AuthGuard
import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.components.widgets.BackBar
import co.maytes.standupbot.dashboard.components.widgets.BackBarJump
import co.maytes.standupbot.dashboard.features.rundetail.RunDetailContainer
import co.maytes.standupbot.dashboard.features.rundetail.RunDetailContract
import co.maytes.standupbot.dashboard.features.standupruns.RunStatusRules
import co.maytes.standupbot.dashboard.features.standupruns.StandupDetailView
import co.maytes.standupbot.dashboard.features.standupruns.rememberStandupRunsStore
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.shared.dto.auth.RoleDto
import com.kttipay.mvi.compose.subscribe
import co.maytes.standupbot.dashboard.styles.Spacing
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.px

@Page("/app/runs/{id}")
@Composable
fun StandupDetailPage() = AuthGuard {
    AppShell {
        RunDetailScreen()
    }
}

@Composable
private fun RunDetailScreen() {
    val api = rememberApiClient()
    val auth = rememberAuthStore()
    val ui = rememberUiStore()
    val ctx = rememberPageContext()
    val runsStore = rememberStandupRunsStore()
    val scope = rememberCoroutineScope()

    val runId = ctx.route.params["id"] ?: ""
    val workspaceId = auth.currentUser?.workspace?.id
    val role = auth.currentUser?.role
    val isAdmin = role == RoleDto.OWNER || role == RoleDto.ADMIN

    val container = remember(api, runsStore) { RunDetailContainer(scope, api, runsStore) }

    val state by container.subscribe { action ->
        when (action) {
            is RunDetailContract.Action.Navigate -> ctx.router.navigateTo(action.path)
            is RunDetailContract.Action.Toast -> ui.showToast(
                action.message,
                if (action.error) ToastKind.Error else ToastKind.Success,
            )
        }
    }

    LaunchedEffect(workspaceId, runId) {
        if (!workspaceId.isNullOrBlank() && runId.isNotBlank()) {
            container.intent(RunDetailContract.Intent.Load(workspaceId, runId))
        }
    }

    val runStatus = state.detail?.status
    LaunchedEffect(workspaceId, runId, runStatus) {
        val ws = workspaceId
        if (ws.isNullOrBlank() || runId.isBlank()) return@LaunchedEffect
        val pollIntervalMs = RunStatusRules.pollIntervalMs(runStatus) ?: return@LaunchedEffect
        while (true) {
            delay(pollIntervalMs)
            runsStore.invalidateDetail(runId)
            container.intent(RunDetailContract.Intent.Load(workspaceId = ws, runId = runId))
        }
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .maxWidth(1200.px)
            .styleModifier { property("margin", "0 auto") },
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            val standupJump = state.detail?.standupId?.let { sid ->
                BackBarJump(label = state.detail?.standupName ?: "Standup", path = "/app/standups/$sid")
            }
            BackBar(
                fallbackPath = "/app/runs",
                jumpTo = standupJump,
            )

            StateSwap(state = state.phase) {
                when (val status = state.status) {
                    RunDetailContract.Status.Loading -> DetailSkeleton()
                    is RunDetailContract.Status.Error -> ApiErrorView(
                        error = status.error,
                        resourceLabel = "standup",
                        onRetry = { container.intent(RunDetailContract.Intent.Retry) },
                        onBack = { container.intent(RunDetailContract.Intent.BackToList) },
                    )
                    RunDetailContract.Status.Ready -> {
                        val detail = state.detail
                        if (detail != null) {
                            StandupDetailView(
                                detail = detail,
                                isAdmin = isAdmin,
                                rerunInFlight = state.rerunInFlight,
                                currentUserId = auth.currentUser?.userId ?: "",
                                workspaceId = workspaceId ?: "",
                                standupId = runId,
                                store = runsStore,
                                ui = ui,
                                regenerateInFlight = state.regenerateInFlight,
                                regenerateError = state.regenerateError,
                                onDismissRegenerateError = {
                                    container.intent(RunDetailContract.Intent.DismissRegenerateError)
                                },
                                onRegenerate = { model ->
                                    container.intent(RunDetailContract.Intent.Regenerate(model))
                                },
                                onRerun = { container.intent(RunDetailContract.Intent.Rerun) },
                                onPostDraftNow = { container.intent(RunDetailContract.Intent.PostDraftNow) },
                                onDiscardDraft = { container.intent(RunDetailContract.Intent.DiscardDraft) },
                                draftActionInFlight = state.draftActionInFlight,
                                rawActivityRetentionDays = auth.currentUser?.workspace?.rawActivityRetentionDays ?: 30,
                            )
                        } else {
                            DetailSkeleton()
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun DetailSkeleton() {
    Column(modifier = Modifier.fillMaxWidth()) {
        SkeletonCard {
            com.varabyte.kobweb.compose.foundation.layout.Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = com.varabyte.kobweb.compose.ui.Alignment.CenterVertically,
                horizontalArrangement = com.varabyte.kobweb.compose.foundation.layout.Arrangement.SpaceBetween,
            ) {
                Column(modifier = Modifier.styleModifier { property("gap", "10px"); property("min-width", "0") }) {
                    SkeletonLine(widthPct = 54, heightPx = 22)
                    SkeletonLine(widthPct = 34, heightPx = 12)
                }
                SkeletonBox(widthPx = 104, heightPx = 36, radiusPx = 8)
            }
        }
        com.varabyte.kobweb.compose.foundation.layout.Row(
            modifier = Modifier.fillMaxWidth().margin(top = Spacing.md).styleModifier { property("gap", "20px") },
        ) {
            repeat(3) { SkeletonBox(widthPx = 88, heightPx = 14, radiusPx = 6) }
        }
        Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.lg)) {
            SkeletonCard {
                Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "12px") }) {
                    SkeletonLine(widthPct = 30, heightPx = 14)
                    SkeletonLine(widthPct = 100, heightPx = 12)
                    SkeletonLine(widthPct = 92, heightPx = 12)
                    SkeletonLine(widthPct = 80, heightPx = 12)
                    Box(modifier = Modifier.margin(top = Spacing.sm)) {
                        SkeletonLine(widthPct = 30, heightPx = 14)
                    }
                    SkeletonLine(widthPct = 96, heightPx = 12)
                    SkeletonLine(widthPct = 70, heightPx = 12)
                }
            }
        }
    }
}
